/**
 * Where the drawer keeps what it folds, so a reload does not lose the shift.
 *
 * Specification: ../test/drawer.spec.ts ("a reload"). Master plan §4 M8, §3 K4.
 *
 * SEPARATE FROM THE OUTBOX, and that is the whole reason it exists. The outbox
 * is a TRANSMISSION queue — an event leaves it the moment the server
 * acknowledges — so on a till that has been draining happily all evening it
 * holds nothing, and a drawer rebuilt from it would report a shift that took no
 * money.
 *
 * A LOCAL READ MODEL, NOT A SECOND SOURCE OF TRUTH. The server still derives
 * its own expectation by folding the event log, and the two still have to
 * agree; `e2e/test/c-shift-through-the-api.spec.ts` is what holds them to it.
 * Nothing here is ever sent anywhere.
 *
 * Same `SqliteDriver` port as the outbox, so the same OPFS database, the same
 * durability settings, and no second thing to open.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";
import { decodePayload, encodePayload } from "@gf/kernel/json";
import type { OutboundEvent } from "@gf/sync-client";
import type { DrawerJournal } from "./drawer";

interface Row {
  readonly event_id: string;
  readonly stream_id: string;
  readonly type: string;
  readonly payload_json: string;
  readonly hlc: string;
  readonly occurred_at: number;
}

export class SqliteDrawerJournal implements DrawerJournal {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /** Idempotent: every app start runs it. */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS drawer_journal (
        seq         INTEGER PRIMARY KEY AUTOINCREMENT,
        event_id    TEXT    NOT NULL UNIQUE,
        stream_id   TEXT    NOT NULL,
        type        TEXT    NOT NULL,
        payload_json TEXT   NOT NULL,
        hlc         TEXT    NOT NULL,
        occurred_at INTEGER NOT NULL
      )
    `);
  }

  async append(event: OutboundEvent): Promise<void> {
    await this.#driver.run(
      `INSERT INTO drawer_journal (event_id, stream_id, type, payload_json, hlc, occurred_at)
       VALUES (?, ?, ?, ?, ?, ?)
       ON CONFLICT (event_id) DO NOTHING`,
      [
        event.eventId,
        event.streamId,
        event.type,
        // THE TAGGED CODEC. A tender payload carries money as bigint and
        // `JSON.stringify` throws outright on one — the same line that 500'd
        // every tender on the server until the event log was fixed.
        encodePayload(event.payload),
        event.hlc,
        event.occurredAt.getTime(),
      ],
    );
  }

  /**
   * Everything, oldest first.
   *
   * ORDER IS THE CONTRACT. The fold applies `shift.opened` before anything
   * else, and a reversal has to arrive after the tender it reverses — so the
   * insertion order is what makes a replay mean the same thing the original
   * evening meant. AUTOINCREMENT rather than a timestamp: two events in the
   * same millisecond are ordinary, and a device clock can move backwards.
   */
  async load(): Promise<OutboundEvent[]> {
    const rows = await this.#driver.all<Row>(
      `SELECT event_id, stream_id, type, payload_json, hlc, occurred_at
         FROM drawer_journal ORDER BY seq`,
      [],
    );

    return rows.map((row) => ({
      eventId: row.event_id,
      streamId: row.stream_id,
      type: row.type,
      payload: decodePayload(row.payload_json),
      hlc: row.hlc,
      occurredAt: new Date(row.occurred_at),
    }));
  }

  /**
   * Emptied when a new shift opens.
   *
   * Safe because these rows are a local view: the events themselves are already
   * in the outbox or on the server. Keeping them would make tomorrow's drawer
   * inherit yesterday's float and takings, and a variance nobody can explain is
   * worse than no variance report at all.
   */
  async reset(): Promise<void> {
    await this.#driver.exec("DELETE FROM drawer_journal");
  }
}
